/**
 * HomeProblemSection — mirrored before/after flow.
 * Before: heading left, problems right → After: promise left, preview right.
 */

import { motion, useScroll, useSpring, useTransform } from 'framer-motion';
import { Sparkles, XCircle } from 'lucide-react';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { HeroPreviewCopyProvider, useHeroPreviewCopy } from './useHeroPreviewCopy';

function ListPanel({ compact, children }: { compact?: boolean; children: React.ReactNode }) {
  return (
    <div className={`home-problem-section__mesh${compact ? ' home-problem-section__mesh--compact' : ''}`}>
      <div className="hiw-mesh-layer" aria-hidden />
      <div className="home-problem-section__mesh-inner">{children}</div>
    </div>
  );
}

function TimelineLine({
  sectionRef,
  colorClass,
}: {
  sectionRef: React.RefObject<HTMLDivElement | null>;
  colorClass: string;
}) {
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start 0.85', 'end 0.2'],
  });
  const smooth = useSpring(scrollYProgress, { stiffness: 52, damping: 22 });
  const lineH = useTransform(smooth, [0, 1], ['0%', '100%']);

  return (
    <>
      <div
        className="pointer-events-none absolute bottom-0 left-0 top-0 w-0.5 opacity-10"
        style={{ background: 'currentColor' }}
        aria-hidden
      />
      <motion.div
        className={`home-problem-section__timeline-fill pointer-events-none absolute left-0 top-0 w-0.5 rounded-full ${colorClass}`}
        style={{ height: lineH }}
        aria-hidden
      />
    </>
  );
}

function LineCard({ gradient, children }: { gradient: string; children: React.ReactNode }) {
  return (
    <div
      style={{ background: gradient, padding: '1px' }}
      className="home-problem-section__line-card-wrap rounded-xl"
    >
      <div className="home-problem-section__line-card-inner rounded-[11px] bg-white px-3.5 py-3">
        {children}
      </div>
    </div>
  );
}

function AgentPreviewStatic() {
  const p = useHeroPreviewCopy();
  return (
    <div className="home-problem-section__agent-preview">
      <div className="home-problem-section__agent-preview-header">
        <Sparkles className="h-4 w-4 text-[#a68b5b]" strokeWidth={2} aria-hidden />
        <span>{p.breadcrumbAgent}</span>
        <span className="home-problem-section__agent-preview-live">{p.liveUpdates}</span>
      </div>
      <div className="home-problem-section__agent-preview-body">
        <span className="home-problem-section__agent-preview-you">{p.you}</span>
        <div className="home-problem-section__agent-preview-input">{p.agentQuery}</div>
      </div>
    </div>
  );
}

function WorkspacePreviewCard() {
  const { t } = useTranslation();

  return (
    <>
      <h3 className="home-problem-section__preview-title">{t('home.problem.lifeAfterTitle')}</h3>
      <HeroPreviewCopyProvider>
        <AgentPreviewStatic />
      </HeroPreviewCopyProvider>
    </>
  );
}

const HomeProblemSection: React.FC = () => {
  const { t } = useTranslation();
  const problemRef = useRef<HTMLDivElement>(null);
  const promiseRef = useRef<HTMLDivElement>(null);

  const problemItems = t('home.problem.problemItems', { returnObjects: true }) as { title: string }[];
  const promiseItems = t('home.problem.promiseItems', { returnObjects: true }) as { n: string; title: string }[];

  return (
    <section id="problem" className="home-problem-section scroll-mt-28">
      <div className="home-problem-section__shell">
        <header className="home-problem-section__hero">
          <p className="home-problem-section__eyebrow">{t('home.problem.eyebrow')}</p>
          <h2 className="home-problem-section__title text-balance">
            {t('home.problem.titleBefore')}
            <span className="home-problem-section__title-accent">{t('home.problem.titleAccent')}</span>
            {t('home.problem.titleAfter')}
          </h2>
          {t('home.problem.subtitle') ? (
            <p className="home-problem-section__subtitle">{t('home.problem.subtitle')}</p>
          ) : null}
        </header>

        <div className="home-problem-section__mirror">
          {/* Hidden on mobile to cut scroll fatigue — only the "Life With" row shows */}
          <div className="home-problem-section__mirror-row home-problem-section__mirror-row--before hidden sm:flex">
            <div className="home-problem-section__mirror-heading">
              <p className="home-problem-section__life-label home-problem-section__life-label--before">
                {t('home.problem.lifeBeforeLabel')}
              </p>
              <h3 className="home-problem-section__life-title">{t('home.problem.lifeBeforeTitle')}</h3>
              <p className="home-problem-section__life-subtitle">{t('home.problem.lifeBeforeSubtitle')}</p>
            </div>

            <ListPanel>
              <div ref={problemRef} className="home-problem-section__mirror-list" style={{ color: '#94a3b8' }}>
                <TimelineLine
                  sectionRef={problemRef}
                  colorClass="home-problem-section__timeline-fill--before bg-gradient-to-b from-slate-300 via-slate-400 to-slate-300"
                />
                <div className="home-problem-section__mirror-list-items">
                  {Array.isArray(problemItems) &&
                    problemItems.map((item, i) => (
                      <motion.div
                        key={item.title}
                        initial={{ opacity: 0, x: 16 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true, margin: '-40px' }}
                        transition={{ duration: 0.32, delay: 0.03 + i * 0.04 }}
                      >
                        <LineCard gradient="linear-gradient(135deg,#e8e0d4 0%,#f5f0e8 55%,#faf7f2 100%)">
                          <div className="flex items-start gap-2.5">
                            <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" strokeWidth={2} aria-hidden />
                            <p className="text-sm font-semibold leading-snug text-gray-800">{item.title}</p>
                          </div>
                        </LineCard>
                      </motion.div>
                    ))}
                </div>
              </div>
            </ListPanel>
          </div>

          <div className="home-problem-section__mirror-row home-problem-section__mirror-row--after">
            <ListPanel>
              <div ref={promiseRef} className="home-problem-section__mirror-list" style={{ color: '#a68b5b' }}>
                <TimelineLine
                  sectionRef={promiseRef}
                  colorClass="home-problem-section__timeline-fill--after bg-gradient-to-b from-stone-300 via-[#c4a574] to-stone-400"
                />
                <div className="home-problem-section__mirror-list-items">
                  {Array.isArray(promiseItems) &&
                    promiseItems.map((item, i) => (
                      <motion.div
                        key={item.n}
                        initial={{ opacity: 0, x: -16 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true, margin: '-40px' }}
                        transition={{ duration: 0.32, delay: 0.04 + i * 0.05 }}
                      >
                        <LineCard gradient="linear-gradient(135deg,#e8dfd0 0%,#f5f0e8 45%,#faf7f2 100%)">
                          <div className="flex items-start gap-2">
                            <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#a68b5b] text-[10px] font-extrabold text-white">
                              {item.n.replace(/^0/, '')}
                            </span>
                            <p className="text-sm font-semibold leading-snug text-gray-900">{item.title}</p>
                          </div>
                        </LineCard>
                      </motion.div>
                    ))}
                </div>
              </div>
            </ListPanel>

            <div className="home-problem-section__mirror-preview">
              <ListPanel compact>
                <WorkspacePreviewCard />
              </ListPanel>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HomeProblemSection;
